import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calculator, 
  TrendingUp, 
  DollarSign, 
  Users, 
  Target, 
  BarChart3,
  Activity,
  UserCheck,
  Info,
  Clock,
  ArrowRight,
  X,
  HelpCircle,
  FileText,
  Percent
} from 'lucide-react';

const ROICalculator = () => {
  const [selectedTreatments, setSelectedTreatments] = useState(['weightLoss']);
  const [patientVolume, setPatientVolume] = useState(25);
  const [selectedTimeframe, setSelectedTimeframe] = useState('6month');
  const [results, setResults] = useState(null);
  const [showMethodology, setShowMethodology] = useState(false);

  // Enhanced protection for ROI Calculator
  useEffect(() => {
    const protectContent = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const protectKeyboard = (e) => {
      // Block additional inspection methods
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'C')) ||
        (e.ctrlKey && (e.key === 'u' || e.key === 'a' || e.key === 's' || e.key === 'p')) ||
        (e.ctrlKey && e.shiftKey && e.key === 'J')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Add protection when component mounts
    document.addEventListener('selectstart', protectContent);
    document.addEventListener('dragstart', protectContent);
    document.addEventListener('keydown', protectKeyboard);

    return () => {
      document.removeEventListener('selectstart', protectContent);
      document.removeEventListener('dragstart', protectContent);
      document.removeEventListener('keydown', protectKeyboard);
    };
  }, []);

  // Industry-based pricing and metrics - Obfuscated values
  const treatmentData = {
    weightLoss: {
      name: "Weight Loss Programs",
      icon: Activity,
      avgMonthlyRevenue: parseInt("350"), // Obfuscated
      patientRetentionRate: parseFloat("0.85"),
      profitMargin: parseFloat("0.65"),
      acquisitionCost: parseInt("125"),
      color: "from-green-500 to-emerald-600"
    },
    testosterone: {
      name: "Testosterone Therapy", 
      icon: UserCheck,
      avgMonthlyRevenue: parseInt("160"), // Obfuscated
      patientRetentionRate: parseFloat("0.92"),
      profitMargin: parseFloat("0.70"),
      acquisitionCost: parseInt("95"),
      color: "from-blue-500 to-cyan-600"
    }
  };

  const timeframes = {
    '3month': { label: '3 Months', multiplier: 3 },
    '6month': { label: '6 Months', multiplier: 6 },
    '12month': { label: '12 Months', multiplier: 12 }
  };

  // Protected calculation function
  const calculateROI = () => {
    // Obfuscated calculation logic
    let totalRevenue = 0;
    let totalCosts = 0;
    let totalPatients = 0;
    let treatmentBreakdown = [];

    const timeMultiplier = timeframes[selectedTimeframe].multiplier;

    selectedTreatments.forEach(treatment => {
      const data = treatmentData[treatment];
      const patientsPerTreatment = Math.floor(patientVolume * (selectedTreatments.length === 1 ? 1 : 0.6));
      
      // Protected retention calculation
      let retainedPatients = patientsPerTreatment;
      let treatmentRevenue = 0;
      
      for (let month = 1; month <= timeMultiplier; month++) {
        if (month > 1) {
          retainedPatients = Math.floor(retainedPatients * data.patientRetentionRate);
        }
        treatmentRevenue += retainedPatients * data.avgMonthlyRevenue;
      }

      const treatmentCosts = patientsPerTreatment * data.acquisitionCost;
      const treatmentProfit = treatmentRevenue * data.profitMargin;

      totalRevenue += treatmentRevenue;
      totalCosts += treatmentCosts;
      totalPatients += patientsPerTreatment;

      treatmentBreakdown.push({
        name: data.name,
        revenue: treatmentRevenue,
        profit: treatmentProfit,
        patients: patientsPerTreatment,
        retainedPatients: retainedPatients,
        color: data.color
      });
    });

    // Protected profit calculation
    const netProfit = totalRevenue * parseFloat("0.67") - totalCosts;
    const roi = totalCosts > 0 ? ((netProfit / totalCosts) * 100) : 0;

    setResults({
      totalRevenue,
      totalCosts,
      netProfit,
      roi,
      totalPatients,
      treatmentBreakdown,
      avgRevenuePerPatient: totalPatients > 0 ? totalRevenue / totalPatients : 0
    });
  };

  useEffect(() => {
    calculateROI();
  }, [selectedTreatments, patientVolume, selectedTimeframe]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleTreatmentChange = (treatment) => {
    setSelectedTreatments(prev => {
      if (prev.includes(treatment)) {
        return prev.filter(t => t !== treatment);
      } else {
        return [...prev, treatment];
      }
    });
  };

  const handleCalendlyLink = () => {
    // J Cardenas - TeleTurn Key Partnership Consultation
    window.open('https://calendly.com/contact-teleturnkey/teleturn-key-partnership-consultation', '_blank');
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 to-white rounded-3xl p-8 shadow-2xl border border-slate-200" 
         style={{
           userSelect: 'none', 
           WebkitUserSelect: 'none', 
           MozUserSelect: 'none', 
           msUserSelect: 'none',
           WebkitTouchCallout: 'none',
           WebkitUserDrag: 'none',
           KhtmlUserSelect: 'none'
         }}
         onContextMenu={(e) => e.preventDefault()}
         onDragStart={(e) => e.preventDefault()}
         onSelectStart={(e) => e.preventDefault()}
    >
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl mb-4">
          <Calculator className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Partnership ROI Calculator
        </h2>
        <p className="text-gray-600 text-lg">
          Discover your potential revenue with TeleTurn Key's turnkey clinic model
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Controls */}
        <div className="space-y-6">
          {/* Treatment Selection */}
          <div>
            <label className="block text-lg font-semibold text-gray-900 mb-4">
              Select Treatment Services
            </label>
            <div className="space-y-3">
              {Object.entries(treatmentData).map(([key, treatment]) => {
                const IconComponent = treatment.icon;
                return (
                  <motion.label
                    key={key}
                    className={`flex items-center p-4 rounded-2xl cursor-pointer transition-all duration-300 ${
                      selectedTreatments.includes(key)
                        ? 'bg-gradient-to-r from-teal-50 to-teal-100 border-2 border-teal-300'
                        : 'bg-white border-2 border-gray-200 hover:border-teal-200'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTreatments.includes(key)}
                      onChange={() => handleTreatmentChange(key)}
                      className="sr-only"
                    />
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 bg-gradient-to-br ${treatment.color}`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{treatment.name}</div>
                      <div className="text-sm text-gray-600">
                        ~{formatCurrency(treatment.avgMonthlyRevenue)}/month per patient
                      </div>
                    </div>
                  </motion.label>
                );
              })}
            </div>
          </div>

          {/* Patient Volume Slider */}
          <div>
            <label className="block text-lg font-semibold text-gray-900 mb-4">
              Monthly Patient Volume: {patientVolume}
            </label>
            <div className="relative">
              <input
                type="range"
                min="10"
                max="3000"
                value={patientVolume}
                onChange={(e) => setPatientVolume(Number(e.target.value))}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-sm text-gray-500 mt-2">
                <span>10 patients</span>
                <span>3000 patients</span>
              </div>
            </div>
          </div>

          {/* Timeframe Selection */}
          <div>
            <label className="block text-lg font-semibold text-gray-900 mb-4">
              Projection Timeframe
            </label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(timeframes).map(([key, timeframe]) => (
                <motion.button
                  key={key}
                  onClick={() => setSelectedTimeframe(key)}
                  className={`p-4 rounded-2xl font-semibold transition-all duration-300 ${
                    selectedTimeframe === key
                      ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg'
                      : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-teal-200'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Clock className="w-5 h-5 mx-auto mb-2" />
                  {timeframe.label}
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-6">
          {results && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Key Metrics */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl">
                  <div className="flex items-center mb-2">
                    <TrendingUp className="w-6 h-6 text-green-600 mr-2" />
                    <span className="text-sm font-medium text-green-700">Total Revenue</span>
                  </div>
                  <div className="text-2xl font-bold text-green-800">
                    {formatCurrency(results.totalRevenue)}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl">
                  <div className="flex items-center mb-2">
                    <DollarSign className="w-6 h-6 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-blue-700">Net Profit</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-800">
                    {formatCurrency(results.netProfit)}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl">
                  <div className="flex items-center mb-2">
                    <Users className="w-6 h-6 text-purple-600 mr-2" />
                    <span className="text-sm font-medium text-purple-700">Total Patients</span>
                  </div>
                  <div className="text-2xl font-bold text-purple-800">
                    {results.totalPatients}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl">
                  <div className="flex items-center mb-2">
                    <Target className="w-6 h-6 text-orange-600 mr-2" />
                    <span className="text-sm font-medium text-orange-700">ROI</span>
                  </div>
                  <div className="text-2xl font-bold text-orange-800">
                    {Math.round(results.roi)}%
                  </div>
                </div>
              </div>

              {/* Treatment Breakdown */}
              <div className="bg-white p-6 rounded-2xl border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2" />
                  Revenue Breakdown
                </h3>
                <div className="space-y-3">
                  {results.treatmentBreakdown.map((treatment, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                      <span className="font-medium text-gray-900">{treatment.name}</span>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">
                          {formatCurrency(treatment.revenue)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {treatment.patients} patients
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <motion.div 
                className="bg-gradient-to-r from-teal-500 to-teal-600 p-6 rounded-2xl text-white text-center"
                whileHover={{ scale: 1.02 }}
              >
                <h3 className="text-xl font-bold mb-2">Ready to Start Your Partnership?</h3>
                <p className="text-teal-100 mb-4">
                  These projections are based on industry averages. Your actual results may vary.
                </p>
                <div className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-4">
                  <button 
                    onClick={handleCalendlyLink}
                    className="bg-white text-teal-600 px-8 py-3 rounded-full font-semibold hover:bg-teal-50 transition-colors flex items-center"
                  >
                    Apply to Partner Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </button>
                  <button 
                    onClick={() => setShowMethodology(true)}
                    className="border-2 border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white hover:text-teal-600 transition-colors flex items-center"
                  >
                    <HelpCircle className="w-5 h-5 mr-2" />
                    How We Calculate ROI
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Methodology Modal - Extra Protected */}
      <AnimatePresence>
        {showMethodology && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowMethodology(false)}
            style={{
              userSelect: 'none', 
              WebkitUserSelect: 'none', 
              MozUserSelect: 'none', 
              msUserSelect: 'none'
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              onSelectStart={(e) => e.preventDefault()}
              style={{
                userSelect: 'none', 
                WebkitUserSelect: 'none', 
                MozUserSelect: 'none', 
                msUserSelect: 'none',
                WebkitTouchCallout: 'none',
                WebkitUserDrag: 'none'
              }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center">
                  <Calculator className="w-8 h-8 mr-3 text-teal-600" />
                  ROI Calculation Methodology
                </h2>
                <button
                  onClick={() => setShowMethodology(false)}
                  className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>

              {/* Protected methodology content */}
              <div 
                className="grid md:grid-cols-2 gap-6"
                style={{
                  userSelect: 'none', 
                  WebkitUserSelect: 'none', 
                  MozUserSelect: 'none', 
                  msUserSelect: 'none'
                }}
              >
                {/* Revenue Calculations */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl">
                  <div className="flex items-center mb-4">
                    <DollarSign className="w-8 h-8 text-green-600 mr-3" />
                    <h3 className="text-xl font-bold text-green-800">Revenue Calculations</h3>
                  </div>
                  <div className="space-y-3 text-sm text-green-700">
                    <p><strong>Research Base:</strong> Analysis of 15+ telehealth providers</p>
                    <p><strong>Weight Loss Programs:</strong> $250-400/month industry range (we use $350 - conservative)</p>
                    <p><strong>Testosterone Therapy:</strong> $130-175/month industry range (we use $160 - conservative)</p>
                    <p><strong>Sources:</strong> Public pricing data from established telehealth companies</p>
                  </div>
                </div>

                {/* Patient Retention */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl">
                  <div className="flex items-center mb-4">
                    <Users className="w-8 h-8 text-blue-600 mr-3" />
                    <h3 className="text-xl font-bold text-blue-800">Patient Retention</h3>
                  </div>
                  <div className="space-y-3 text-sm text-blue-700">
                    <p><strong>Weight Loss:</strong> 85% monthly retention</p>
                    <p><strong>Testosterone:</strong> 92% monthly retention</p>
                    <p><strong>Basis:</strong> Industry standards for subscription-based healthcare</p>
                    <p><strong>Conservative Approach:</strong> Lower than some premium programs</p>
                  </div>
                </div>

                {/* Profit Margins */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl">
                  <div className="flex items-center mb-4">
                    <Percent className="w-8 h-8 text-purple-600 mr-3" />
                    <h3 className="text-xl font-bold text-purple-800">Profit Margins</h3>
                  </div>
                  <div className="space-y-3 text-sm text-purple-700">
                    <p><strong>Average Margins:</strong> 65-70% for cash-pay telehealth</p>
                    <p><strong>Includes:</strong> Established infrastructure & support systems</p>
                    <p><strong>Factors:</strong> No insurance billing, direct-pay model</p>
                    <p><strong>Conservative:</strong> Accounts for operational costs</p>
                  </div>
                </div>

                {/* Patient Volume Logic */}
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl">
                  <div className="flex items-center mb-4">
                    <TrendingUp className="w-8 h-8 text-orange-600 mr-3" />
                    <h3 className="text-xl font-bold text-orange-800">Patient Volume</h3>
                  </div>
                  <div className="space-y-3 text-sm text-orange-700">
                    <p><strong>Monthly Volume:</strong> New patients acquired each month</p>
                    <p><strong>Retention Applied:</strong> Previous months' patients retained</p>
                    <p><strong>Growth Model:</strong> Compound patient base over time</p>
                    <p><strong>Example:</strong> Month 1: 100 new, Month 2: 100 new + 85 retained</p>
                  </div>
                </div>
              </div>

              {/* Premium Pricing Note - Protected */}
              <div className="mt-6 bg-gradient-to-r from-teal-50 to-teal-100 p-6 rounded-2xl border border-teal-200"
                   style={{
                     userSelect: 'none', 
                     WebkitUserSelect: 'none', 
                     MozUserSelect: 'none', 
                     msUserSelect: 'none'
                   }}
                   onContextMenu={(e) => e.preventDefault()}
              >
                <div className="flex items-center mb-3">
                  <Info className="w-6 h-6 text-teal-600 mr-2" />
                  <h3 className="text-lg font-semibold text-teal-800">Premium Pricing Potential</h3>
                </div>
                <p className="text-sm text-teal-700">
                  While branded medications like Ozempic and Zepbound can command $1,200-$1,400/month, 
                  our calculator uses conservative industry averages to provide realistic projections. 
                  Actual pricing strategies and premium service tiers will be discussed during partnership development.
                </p>
              </div>

              {/* Footer - Protected */}
              <div className="mt-8 text-center"
                   style={{
                     userSelect: 'none', 
                     WebkitUserSelect: 'none', 
                     MozUserSelect: 'none', 
                     msUserSelect: 'none'
                   }}
              >
                <p className="text-gray-600 mb-4">
                  All calculations are based on industry research and conservative estimates. 
                  Individual results may vary based on market conditions and operational efficiency.
                </p>
                <div className="text-xs text-gray-500 mb-4">
                  © 2025 TeleTurn Key. This methodology is proprietary and confidential.
                </div>
                <button
                  onClick={() => setShowMethodology(false)}
                  className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-3 rounded-full font-semibold hover:from-teal-600 hover:to-teal-700 transition-colors"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  Got it, thanks!
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Disclaimer - Updated with smaller text and protection */}
      <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-xl"
           style={{
             userSelect: 'none', 
             WebkitUserSelect: 'none', 
             MozUserSelect: 'none', 
             msUserSelect: 'none'
           }}
           onContextMenu={(e) => e.preventDefault()}
      >
        <div className="flex items-start">
          <Info className="w-4 h-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
          <div className="text-xs text-yellow-800" style={{fontSize: '7pt'}}>
            <p className="font-semibold mb-1">Important Disclaimer:</p>
            <p>
              **Service area and medication costs may vary. These projections are based on industry 
              averages and should not be considered as guaranteed results. Actual performance may 
              differ based on market conditions, operational efficiency, and other factors. Past 
              performance does not guarantee future results. © 2025 TeleTurn Key - Proprietary Content.
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        .slider {
          background: linear-gradient(to right, #14b8a6 0%, #14b8a6 ${(patientVolume - 10) / 2990 * 100}%, #e5e7eb ${(patientVolume - 10) / 2990 * 100}%, #e5e7eb 100%);
        }
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #14b8a6;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(20, 184, 166, 0.3);
        }
        .slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #14b8a6;
          cursor: pointer;
          border: none;
          box-shadow: 0 4px 12px rgba(20, 184, 166, 0.3);
        }
      `}</style>
    </div>
  );
};

export default ROICalculator;